<script lang="ts">
import {
    formatDirectBuilderImageSrcsetForAspectRatio,
    formatDirectBuilderImageUrlForAspectRatio,
} from '$lib/utils/formatImageUrl';
import { EHeroAspectRatios, ETitleFontSizes } from '../../../config/enums';
import HeroText from '../HeroText/HeroText.svelte';

interface HeroImageData {
    src?: string;
    altText?: string;
    alt?: string;
}

interface Props {
    content?: string;
    subContent?: string;
    mastheadTextColour?: string;
    image?: HeroImageData;
    mobileImage?: HeroImageData;
    link?: string;
    linkText?: string;
    link2?: string;
    linkText2?: string;
    inverseButtonColours?: boolean;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    titleSize?: string;
    aspectRatio?: string;
}

let {
    content = '',
    subContent = '',
    mastheadTextColour = '#000',
    image = $bindable({}),
    mobileImage = $bindable({}),
    link = '',
    linkText = 'Learn More',
    link2 = '',
    linkText2 = 'Learn More',
    inverseButtonColours = false,
    headingLevel = 'h1',
    titleSize = ETitleFontSizes.H2,
    aspectRatio = EHeroAspectRatios.DEFAULT,
}: Props = $props();

const DESKTOP_IMAGE_WIDTHS = [960, 1280, 1600, 1920] as const;
const MOBILE_IMAGE_WIDTHS = [360, 480, 640] as const;
const HERO_IMAGE_PARAMS = 'format=webp&quality=56';
const HERO_IMAGE_DESKTOP_ASPECT_RATIO = 3 / 1;
const HERO_IMAGE_SHOWCASE_ASPECT_RATIO = 12 / 5;
const HERO_IMAGE_MOBILE_ASPECT_RATIO = 1 / 1;
const imageAltText = $derived(image.altText || image.alt || '');
const desktopAspectRatio = $derived(
    aspectRatio === EHeroAspectRatios.SHOWCASE
        ? HERO_IMAGE_SHOWCASE_ASPECT_RATIO
        : HERO_IMAGE_DESKTOP_ASPECT_RATIO,
);
const effectiveMobileImage = $derived(
    mobileImage && Object.keys(mobileImage).length > 0 ? mobileImage : image,
);
const mobileImageAltText = $derived(
    effectiveMobileImage.altText || effectiveMobileImage.alt || imageAltText,
);
const desktopImageSrc = $derived(
    formatDirectBuilderImageUrlForAspectRatio(
        image.src,
        1280,
        desktopAspectRatio,
        HERO_IMAGE_PARAMS,
    ),
);
const desktopImageSrcset = $derived(
    formatDirectBuilderImageSrcsetForAspectRatio(
        image.src,
        DESKTOP_IMAGE_WIDTHS,
        desktopAspectRatio,
        HERO_IMAGE_PARAMS,
    ),
);
const mobileImageSrc = $derived(
    formatDirectBuilderImageUrlForAspectRatio(
        effectiveMobileImage.src,
        480,
        HERO_IMAGE_MOBILE_ASPECT_RATIO,
        HERO_IMAGE_PARAMS,
    ),
);
const mobileImageSrcset = $derived(
    formatDirectBuilderImageSrcsetForAspectRatio(
        effectiveMobileImage.src,
        MOBILE_IMAGE_WIDTHS,
        HERO_IMAGE_MOBILE_ASPECT_RATIO,
        HERO_IMAGE_PARAMS,
    ),
);
</script>

<div class="hero-image hero-image--aspect-{aspectRatio}">
    <div class="hero-image__wrapper hero-image__wrapper--desktop">
        {#if desktopImageSrc}
            <img
                src={desktopImageSrc}
                srcset={desktopImageSrcset}
                sizes="100vw"
                alt={imageAltText}
                class="image"
                fetchpriority="high"
                decoding="async"
            />
        {/if}
    </div>
    {#if mobileImageSrc}
        <div class="hero-image__wrapper hero-image__wrapper--mobile">
            <img
                src={mobileImageSrc}
                srcset={mobileImageSrcset}
                sizes="100vw"
                alt={mobileImageAltText}
                class="image"
                fetchpriority="high"
                decoding="async"
            />
        </div>
    {/if}
    {#if content || link}
        <HeroText
            {mastheadTextColour}
            {content}
            {subContent}
            {link}
            {linkText}
            {link2}
            {linkText2}
            {inverseButtonColours}
            {headingLevel}
            {titleSize}
        />
    {/if}
</div>

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .hero-image {
        aspect-ratio: var(--layout-hero-ar); // duplicated from the image so we can do position:fixed header
        position: relative;

        &--aspect-showcase {
            aspect-ratio: var(--layout-hero-ar-showcase);
        }

        &__wrapper {
            // position: fixed;
            width: 100%;

            &--desktop {
                display: none;
                @include mq($from: tablet) {
                    display: block;
                }
            }
            &--mobile {
                display: block;
                @include mq($from: tablet) {
                    display: none;
                }
            }
        }
    }

    .hero-image__wrapper :global(.image) {
        aspect-ratio: var(--layout-hero-ar);
        object-fit: cover;
        width: 100%;
    }

    .hero-image--aspect-showcase .hero-image__wrapper :global(.image) {
        aspect-ratio: var(--layout-hero-ar-showcase);
    }

    // target the relevant .builder-block divs for overlaying the fixed header
    :global(.builder-block:has(> div > .hero-image) ~ .builder-block) {
        background-color: var(--colour-white);
        z-index: 1;
    }

</style>
